import React from "react";
import { IonModal, IonButton, IonIcon, IonToggle } from "@ionic/react";
import { close } from "ionicons/icons";
import BiWazeGoalModal from "../../components/ui/popups/BiwazeGoals";
import DatePickerWheel from "../../components/ui/Sliders/DatePickerWheel";
import {
  getSelectedTime24,
  hoursArray,
  meridianArray,
  minutesArray,
} from "../../utils/date";

// TYPES AND INTERFACES
export interface User {
  id: number;
  name: string;
  enabled: boolean;
}

export interface NotificationItem {
  title: string;
  subtitle: string;
}

export interface TherapySettings {
  dailyReminder: {
    time: string;
    enabled: boolean;
  };
  missedDays: {
    count: number;
    enabled: boolean;
  };
  pressureThresholds: {
    value: number;
    enabled: boolean;
  };
}

export interface PushNotificationSettings {
  enabled: boolean;
  deliveryType: string;
}

export interface GoalData {
  target_count: number;
  target_seconds: number;
  target_hours: number;
  isActive: boolean;
}

export type ModalType =
  | "dailyReminder"
  | "missedDays"
  | "pressureThresholds"
  | "pushSettings"
  | string
  | null;

export const formatTime = (time24: string): string => {
  if (!time24) return "Off";

  const [hours, minutes] = time24.split(":");
  const hourNum = parseInt(hours);
  const hour12 = hourNum === 0 ? 12 : hourNum > 12 ? hourNum - 12 : hourNum;
  const ampm = hourNum >= 12 ? "PM" : "AM";

  return `${hour12}:${minutes} ${ampm}`;
};

export const createSaveData = (
  userId: string | null,
  therapySettings: TherapySettings,
  pushNotificationEnabled: boolean
) => {
  return {
    set_for: userId,
    set_by_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    is_notification_enabled: pushNotificationEnabled,
    is_daily_reminder_enabled: therapySettings.dailyReminder.enabled,
    daily_reminder_time: therapySettings.dailyReminder.time,
    pressure_threshold: therapySettings.pressureThresholds.value,
    missed_days_count: therapySettings.missedDays.count,
    track_pressure_alert: therapySettings.pressureThresholds.enabled,
    track_missed_days: therapySettings.missedDays.enabled,
  };
};

export const createGoalDataFromResponse = (goalData: any) => {
  const missedDaysGoalData = {
    target_count: goalData.missed_days_count || 3,
    target_seconds: 0,
    target_hours: 0,
    isActive: goalData.track_missed_days ?? true,
  };

  const pressureThresholdGoalData = {
    target_count: goalData.pressure_threshold || 10,
    target_seconds: 0,
    target_hours: 0,
    isActive: goalData.track_pressure_alert ?? true,
  };

  const therapySettings = {
    missedDays: {
      count: goalData.missed_days_count || 3,
      enabled: goalData.track_missed_days ?? true,
    },
    pressureThresholds: {
      value: goalData.pressure_threshold || 10,
      enabled: goalData.track_pressure_alert ?? true,
    },
    dailyReminder: {
      time: goalData.daily_reminder_time || "20:00",
      enabled: goalData.is_daily_reminder_enabled ?? true,
    },
  };

  return {
    missedDaysGoalData,
    pressureThresholdGoalData,
    therapySettings,
  };
};

export const getDefaultGoalData = () => {
  return {
    missedDaysGoalData: {
      target_count: 3,
      target_seconds: 0,
      target_hours: 0,
      isActive: true,
    },
    pressureThresholdGoalData: {
      target_count: 10,
      target_seconds: 0,
      target_hours: 0,
      isActive: true,
    },
  };
};

export const deliveryOptions = [
  { value: "Immediate Delivery", label: "Immediate Delivery" },
  { value: "Daily Digest", label: "Daily Digest" },
  { value: "Weekly Summary", label: "Weekly Summary" },
];

// MODAL COMPONENTS
interface GoalModalProps {
  showGoalModal: boolean;
  currentModalType: ModalType;
  missedDaysGoal: GoalData | null;
  pressureThresholdGoal: GoalData | null;
  handleCloseModal: () => void;
  handleGoalSave: (updatedGoal: any) => Promise<void>;
}

export const renderGoalModal = ({
  showGoalModal,
  currentModalType,
  missedDaysGoal,
  pressureThresholdGoal,
  handleCloseModal,

  handleGoalSave,
}: GoalModalProps) => {
  if (!showGoalModal) return null;

  if (currentModalType === "missedDays" && missedDaysGoal) {
    return (
      <BiWazeGoalModal
        autoPresentOnMount={true}
        onClose={handleCloseModal}
        deviceName="Missed Days"
        showNumbers={true}
        showMinutes={false}
        toggleTitle="Send notification"
        subTitle="Notify me when I have missed"
        noOfTimes="Days"
        position="middle"
        goalData={{
          target_count: missedDaysGoal.target_count || 3,
          target_seconds: 0,
          target_hours: 0,
          isActive: missedDaysGoal.isActive ?? true,
        }}
        subText="of BiWaze therapies"
        showRemove={false}
        onSave={handleGoalSave}
      />
    );
  }

  if (currentModalType === "pressureThresholds" && pressureThresholdGoal) {
    return (
      <BiWazeGoalModal
        autoPresentOnMount={true}
        onClose={handleCloseModal}
        deviceName="Pressure Thresholds"
        showNumbers={false}
        toggleTitle="Send notification"
        subTitle="Notify me when my pressures are"
        noOfTimes={
          <>
            cm H<sub>2</sub>O
          </>
        }
        position="middle"
        goalData={{
          target_count: pressureThresholdGoal.target_count || 10,
          target_seconds: 0,
          target_hours: 0,
          isActive: pressureThresholdGoal.isActive ?? true,
        }}
        subText="off from set pressures"
        showRemove={false}
        showCounter={true}
        onSave={handleGoalSave}
      />
    );
  }

  return null;
};

interface CustomModalProps {
  showCustomModal: boolean;
  currentModalType: ModalType;
  handleCloseModal: () => void;
  handleGoalSave: (updatedGoal: any) => Promise<void>;
  navigateToSystemSettings: () => Promise<void>;
  // Daily Reminder props
  isReminderEnabled: boolean;
  setIsReminderEnabled: (enabled: boolean) => void;
  updateDailyReminder: (enabled: boolean, time?: string) => void;
  hrs: number;
  setHrs: (hrs: number) => void;
  minutes: number;
  setMinutes: (minutes: number) => void;
  meridian: number;
  setMeridian: (meridian: number) => void;
  // Push settings props
  pushNotificationSettings: PushNotificationSettings;
  updatePushSettings: (
    enabled: boolean,
    deliveryType: string,
    source?: string
  ) => void;
  followingList: User[];
  updateFollowingSettings: (userId: number, enabled: boolean) => void;
}

export const renderCustomModal = ({
  showCustomModal,
  currentModalType,
  handleCloseModal,
  handleGoalSave,
  navigateToSystemSettings,
  isReminderEnabled,
  setIsReminderEnabled,
  updateDailyReminder,
  hrs,
  setHrs,
  minutes,
  setMinutes,
  meridian,
  setMeridian,
  pushNotificationSettings,
  updatePushSettings,
  followingList,
  updateFollowingSettings,
}: CustomModalProps) => {
  if (!showCustomModal) return null;

  const onSave = () => {
    const selectedTime = getSelectedTime24(hrs, minutes, meridian);
    if (handleGoalSave) {
      handleGoalSave(selectedTime);
    }
  };

  let title = "";
  let content = null;

  // Daily Reminder modal with time picker
  if (currentModalType === "dailyReminder") {
    title = "Daily Reminder";

    content = (
      <>
        <div className="w-full bg-gray-100 rounded-2xl flex justify-between items-center px-4 py-2 mb-6">
          <span
            className="text-xl font-medium"
            style={{
              fontSize: "clamp(1.25rem, 2vw, 1.5rem)",
            }}
          >
            Send Notification
          </span>
          <IonToggle
            checked={isReminderEnabled}
            onIonChange={(e) => {
              setIsReminderEnabled(e.detail.checked);
              updateDailyReminder(e.detail.checked);
            }}
          />
        </div>

        <div className="w-full mb-4">
          <p
            className="text-base mb-2 text-center"
            style={{
              fontFamily: "'Figtree'",
              fontWeight: 400,
              fontSize: "17px",
              lineHeight: "22px",
            }}
          >
            Remind me to track my therapy at
          </p>
          <div className="flex gap-2 justify-center mb-4">
            <DatePickerWheel
              items={hoursArray}
              selectedIndex={hrs}
              onSelect={setHrs}
              label="HOURS"
              isActive={true}
            />
            <DatePickerWheel
              items={minutesArray.map((m: number) =>
                m.toString().padStart(2, "0")
              )}
              selectedIndex={minutes}
              onSelect={setMinutes}
              label="MINUTES"
              isActive={true}
            />
            <DatePickerWheel
              items={meridianArray}
              selectedIndex={meridian}
              onSelect={setMeridian}
              label="AM/PM"
              isActive={true}
            />
          </div>
        </div>
      </>
    );
  }
  // Push Notifications modal with delivery options
  else if (currentModalType === "pushSettings") {
    title = "Push Notifications";
    const { enabled, deliveryType } = pushNotificationSettings;

    content = (
      <>
        <div className="w-full mb-4">
          <p className="text-gray-500">
            Select the notifications you want to receive
          </p>
        </div>

        <div className="w-full bg-gray-100 rounded-lg flex justify-between items-center px-4 py-2 mb-4">
          <span className="text-xl font-medium">Enable Push Notifications</span>
          <IonToggle
            checked={enabled}
            onIonChange={(e) =>
              updatePushSettings(e.detail.checked, deliveryType)
            }
          />
        </div>

        {enabled && (
          <>
            <div className="w-full mb-4">
              <p className="text-base mb-2">Delivery Options</p>
              <div className="flex flex-col w-full gap-2">
                {deliveryOptions.map((option) => (
                  <div
                    key={option.value}
                    className={`w-full p-3 border rounded-lg cursor-pointer 
                    ${
                      deliveryType === option.value
                        ? " border-[#00A3E0]"
                        : "bg-white border-gray-200"
                    }`}
                    onClick={() => updatePushSettings(enabled, option.value)}
                  >
                    <p className="text-center">{option.label}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="w-full">
              <IonButton
                expand="block"
                fill="outline"
                onClick={navigateToSystemSettings}
                className="w-full"
                style={{
                  "--border-radius": "24px",
                  "--border-color": "#00A3E0",
                  "--color": "#00A3E0",
                  height: "44px",
                  marginBottom: "12px",
                }}
              >
                System Notification Settings
              </IonButton>
            </div>
          </>
        )}
      </>
    );
  }
  // Following users modal
  else if (currentModalType && currentModalType.startsWith("following-")) {
    const userId = parseInt(currentModalType.split("-")[1]);
    const user = followingList.find((u) => u.id === userId);

    if (user) {
      title = `Notifications for ${user.name}`;

      content = (
        <>
          <div className="w-full mb-4">
            <p className="text-gray-500">
              Receive notifications when they update their progress
            </p>
          </div>

          <div className="w-full bg-gray-100 rounded-lg flex justify-between items-center px-4 py-2 mb-4">
            <span className="text-xl font-medium">Enable Notifications</span>
            <IonToggle
              checked={user.enabled}
              onIonChange={(e) =>
                updateFollowingSettings(userId, e.detail.checked)
              }
            />
          </div>
        </>
      );
    }
  }

  return (
    <>
      {showCustomModal && (
        <IonModal
          isOpen={showCustomModal}
          onDidDismiss={handleCloseModal}
          className="container-modal"
          style={{
        "--border-radius": "20px",
        "--width": "95%",
        "--height": "auto",
      }}
    >
      <div className="flex flex-col items-center p-4 h-full">
        {/* Header with close button */}
        <div className="w-full flex justify-between items-center m-2">
          <h2 className="text-xl font-bold text-center flex-grow">{title}</h2>
          <button onClick={handleCloseModal} className="text-gray-400">
            <IonIcon icon={close} style={{ fontSize: "24px" }} />
          </button>
        </div>

        {content}

        {/* Save button */}
        <IonButton
          expand="block"
          onClick={onSave}
          className="primary-button m-2 lg:m-4"
        >
          Save
        </IonButton>
      </div>
        </IonModal>
      )}
    </>
  );
};
